import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const LightboxContext = createContext({ openLightbox: () => {} });

export function useLightbox() {
  return useContext(LightboxContext);
}

export function LightboxProvider({ children }) {
  // state shape: { srcs: string[], index: number, onDelete?: (index) => void } or null
  const [state, setState] = useState(null);
  const touchStartXRef = useRef(null);

  /**
   * openLightbox can be called two ways:
   *   openLightbox(src)                                     // single image
   *   openLightbox({ srcs: [...], index: 0, onDelete? })    // carousel
   *
   * If `onDelete(index)` is provided, the lightbox renders a trash button
   * that closes itself first then invokes the callback so the host page can
   * show its own confirmation dialog without z-index conflicts.
   */
  const openLightbox = useCallback((arg) => {
    if (!arg) return;
    if (typeof arg === "string") {
      setState({ srcs: [arg], index: 0, onDelete: null });
      return;
    }
    const srcs = (arg.srcs || []).filter(Boolean);
    if (!srcs.length) return;
    const i = Math.max(0, Math.min(arg.index || 0, srcs.length - 1));
    setState({ srcs, index: i, onDelete: arg.onDelete || null });
  }, []);

  const close = useCallback(() => setState(null), []);

  const goPrev = useCallback(() => {
    setState((s) =>
      s ? { ...s, index: (s.index - 1 + s.srcs.length) % s.srcs.length } : s
    );
  }, []);

  const goNext = useCallback(() => {
    setState((s) =>
      s ? { ...s, index: (s.index + 1) % s.srcs.length } : s
    );
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft" && state.srcs.length > 1) goPrev();
      else if (e.key === "ArrowRight" && state.srcs.length > 1) goNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state, close, goPrev, goNext]);

  const onTouchStart = (e) => {
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartXRef.current;
    touchStartXRef.current = null;
    if (start == null) return;
    const end = e.changedTouches?.[0]?.clientX ?? start;
    const dx = end - start;
    if (Math.abs(dx) < 50 || !state || state.srcs.length < 2) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  const hasMany = !!state && state.srcs.length > 1;
  const canDelete = !!state && typeof state.onDelete === "function";

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!state || !canDelete) return;
    const idx = state.index;
    const cb = state.onDelete;
    // Close first so the host page's confirmation dialog can render on top.
    setState(null);
    setTimeout(() => {
      try { cb(idx); } catch (_) {}
    }, 0);
  };

  return (
    <LightboxContext.Provider value={{ openLightbox }}>
      {children}
      {state && (
        <div
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="fixed inset-0 z-[1000] bg-black/90 backdrop-blur-sm grid place-items-center cursor-zoom-out animate-in fade-in duration-150"
        >
          <img
            key={state.index}
            src={state.srcs[state.index]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl cursor-default animate-in fade-in duration-150"
          />

          {hasMany && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
                data-testid="lightbox-prev"
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
                data-testid="lightbox-next"
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              <div
                data-testid="lightbox-counter"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold backdrop-blur"
                onClick={(e) => e.stopPropagation()}
              >
                {state.index + 1} / {state.srcs.length}
              </div>
            </>
          )}

          {canDelete && (
            <button
              onClick={handleDelete}
              data-testid="lightbox-delete"
              aria-label="Delete this image"
              title="Delete this image"
              className="absolute top-4 right-20 w-10 h-10 grid place-items-center rounded-full bg-red-600 hover:bg-red-700 text-white backdrop-blur transition-colors active:scale-95"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}

          <button
            onClick={close}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl backdrop-blur transition-colors"
          >
            ×
          </button>
        </div>
      )}
    </LightboxContext.Provider>
  );
}

/**
 * Drop-in wrapper that turns any image into a lightbox-opening button.
 * Pass `siblings` (an array of src URLs) + `index` to enable carousel
 * navigation across multiple images of the same post.
 *
 * Pass `onDelete: () => void` to surface a trash button inside the lightbox.
 * The lightbox closes itself before invoking `onDelete` so the caller can
 * show its own confirmation dialog without z-index issues.
 */
export function LightboxImage({
  src,
  fullSrc,
  alt = "",
  className = "",
  testId,
  siblings,
  index,
  onDelete,
  ...imgProps
}) {
  const { openLightbox } = useLightbox();
  if (!src) return null;
  const zoomSrc = fullSrc || src;
  // Wrap onDelete so it ignores the index parameter the lightbox passes
  // (callers that work in single-image mode already know which photo this is).
  const lightboxDelete = onDelete ? () => onDelete() : null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (siblings && siblings.length > 1) {
          openLightbox({
            srcs: siblings,
            index: typeof index === "number" ? index : 0,
            // For carousel mode, the host (PostImageGrid) injects a handler
            // that maps the lightbox-current-index back to the right photo.
            onDelete: onDelete || null,
          });
        } else {
          openLightbox({
            srcs: [zoomSrc],
            index: 0,
            onDelete: lightboxDelete,
          });
        }
      }}
      data-testid={testId}
      className="block w-full p-0 m-0 bg-transparent border-0 cursor-zoom-in"
      aria-label="Open image"
    >
      <img src={src} alt={alt} className={className} {...imgProps} />
    </button>
  );
}
